import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";
import { AuthProvider, useAuth } from "./ScrapFlow/context/AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import "./index.css";
import { syncTheme } from "./utils/themeSync";
import { initNetworkGuard } from "./utils/networkGuard";
import FirstTimeWelcomeScreen from "./ScrapFlow/pages/FirstTimeWelcomeScreen.tsx";
import AppLoadingSkeleton from "./components/AppLoadingSkeleton";
import DeletedAccountLanding from "./app/components/DeletedAccountLanding";

const MyWebsite = lazy(() => import("./MyWebsite"));
const AuthGateway = lazy(() => import("./AuthGateway"));
const DashboardApp = lazy(() => import("./app/DashboardApp"));

// Use your Clerk Publishable Key
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const HAS_SEEN_LANDING_KEY = "hasSeenLandingPage";
const FIRST_TIME_WELCOME_KEY = "hasSeenFirstTimeWelcome";

const getFirstTimeWelcomeKey = (userId) => `${FIRST_TIME_WELCOME_KEY}_${userId || "guest"}`;

const storeReferralCodeFromUrl = () => {
  if (typeof window === "undefined") return;

  try {
    const url = window.location.href;
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const queryReferral = searchParams.get("ref") || "";
    const pathMatch = pathname.match(/^\/invite\/([^\/\?#]+)/i);
    const pathReferral = pathMatch ? decodeURIComponent(pathMatch[1]) : "";
    const pendingReferralCode = queryReferral || pathReferral;
    if (pendingReferralCode) {
      sessionStorage.setItem("pendingReferralCode", pendingReferralCode);
      localStorage.setItem("pendingReferralCode", pendingReferralCode);
    }
  } catch (error) {
    console.warn("[invite-referral] failed to store referral code:", error);
  }
};

storeReferralCodeFromUrl();

function AuthenticatedApp() {
  const {
    isAuthenticated,
    loading,
    sessionRestored,
    handleLogout,
    operatorID,
    userProfile,
    showDeletedAccount,
  } = useAuth();
  const location = useLocation();
  const wasAuthenticatedRef = useRef(false);
  const [dismissedFirstTimeWelcome, setDismissedFirstTimeWelcome] = useState(false);

  // Derived synchronously (not via effect) so the very first render after login
  // already knows whether to show the welcome screen, instead of briefly taking
  // the DashboardApp branch and swapping to the welcome screen a render later.
  const showFirstTimeWelcome =
    !loading &&
    isAuthenticated &&
    !!operatorID &&
    !dismissedFirstTimeWelcome &&
    localStorage.getItem(getFirstTimeWelcomeKey(operatorID)) !== "true";

  useEffect(() => {
    if (!loading && typeof window !== "undefined" && typeof window.__hideAppShell === "function") {
      window.__hideAppShell();
    }
  }, [loading]);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem(HAS_SEEN_LANDING_KEY, "true");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setDismissedFirstTimeWelcome(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      localStorage.setItem(HAS_SEEN_LANDING_KEY, "true");
    }

    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // If we've been marked as a deleted-account device, show the landing immediately
  if (showDeletedAccount) {
    return <DeletedAccountLanding />;
  }

  if (loading || !sessionRestored) {
    // Wait for the persisted session restore to finish before choosing the route.
    return <AppLoadingSkeleton rows={3} showHeader={true} cardHeight={80} />;
  }

  const handleGetStarted = () => {
    if (operatorID) {
      localStorage.setItem(getFirstTimeWelcomeKey(operatorID), "true");
    }
    setDismissedFirstTimeWelcome(true);
  };

  if (isAuthenticated && showFirstTimeWelcome) {
    return (
      <FirstTimeWelcomeScreen
        userName={userProfile?.name || userProfile?.fullName || operatorID || "there"}
        onGetStarted={handleGetStarted}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <ProtectedRoute fallback={null}>
        <DashboardApp />
      </ProtectedRoute>
    );
  }

  const hasSeenLanding = localStorage.getItem(HAS_SEEN_LANDING_KEY) === "true";

  if (!hasSeenLanding) {
    return <MyWebsite />;
  }

  return <AuthGateway />;
}


const root = ReactDOM.createRoot(document.getElementById("root"));

// Ensure global theme defaults to light while we consolidate tokens across the app.
if (typeof window !== "undefined") {
  try {
    const savedThemeMode = localStorage.getItem("themeMode");
    const savedLandingTheme = localStorage.getItem("theme");
    const initialTheme = savedThemeMode || savedLandingTheme || "light";
    syncTheme(initialTheme);
  } catch (e) {
    // ignore
  }

  initNetworkGuard().catch((error) => {
    console.warn("[networkGuard] initialization failed:", error);
  });
}

root.render(
  <BrowserRouter>
    <ClerkProvider publishableKey={clerkPubKey}
    appearance={{
      elements:{
        captcha:{
          containerId:"clerk-captcha"
        },
      },
    }}
    >
      <AuthProvider>
        <Suspense
          fallback={<AppLoadingSkeleton rows={3} showHeader={true} cardHeight={80} />}
        >
          <AuthenticatedApp />
        </Suspense>
      </AuthProvider>
    </ClerkProvider>
  </BrowserRouter>
);
